import type EntityMember from "_shared/entity-members/entity-member";
import { Masked } from "work/masked-fields";
import type WorkAgreement from "work/work-agreements/work-agreement";
import type {
  ProposalCollaborationEnvelope,
  ProposalContentOperation,
  ProposalNegotiationParty,
  ProposalReviewerChange,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  formatProposalChangeLines,
  type ProposalChangeDescriptionContext,
  type ProposalChangeDescriptor,
} from "work/proposals/negotiation/proposal-change-description";
import {
  describeProposalOperations,
  describeProposalReviewerChange,
  reviewerDescriptorPath,
} from "work/proposals/negotiation/proposal-change-descriptors";
import {
  proposalNegotiationRevisesWorkAgreement,
  proposalSelectiveTranchePublicationApplies,
} from "work/proposals/negotiation/proposal-publishable-changes";

/**
 * What a Save or a Submit is about to do, in the user's words: which changes, and who sees them.
 *
 * A save shares with the actor's own side (their reviewers); a submission shares with the counterparty.
 * Saying so beside the change list is the difference between "This will save all pending changes" —
 * which tells the user nothing — and a list they can actually check before it leaves their hands.
 */

export type ProposalChangeSharePlan = {
  descriptors: ProposalChangeDescriptor[];
  lines: string[];
  audienceLabel: string;
  /**
   * How the list splits, so a confirmation message suppresses only the clauses this list actually
   * covers. The dialog learns "there are reviewer changes" from the legacy reviewer state, which is a
   * different source than the queue these descriptors come from; dropping the reviewer clause because
   * SOME list exists is how a reviewer change would end up announced nowhere at all.
   */
  contentChangeCount: number;
  reviewerChangeCount: number;
};

/** Who a Save reaches. Reviewers see saved work immediately; with none, a save stays with the team. */
export function proposalSaveAudienceLabel(args: { reviewerCount: number; isReviewing: boolean }): string {
  if (args.isReviewing) return "your team leader";
  return args.reviewerCount > 0 ? "your reviewers" : "your team";
}

/**
 * What an unsaved change is still waiting for, said by the same rule as `proposalSaveAudienceLabel`.
 *
 * With no reviewers a save reaches nobody new, so "not yet shared" would promise an audience that does
 * not exist (#5812). The change is simply unsaved.
 */
export function proposalUnsavedChangeStatus(args: { reviewerCount: number; isReviewing: boolean }): string {
  if (args.isReviewing) return "not yet shared with your team leader";
  return args.reviewerCount > 0 ? "not yet shared with reviewers" : "not yet saved";
}

/** Who a Submit reaches — always the opposing party, named so there is no doubt. */
export function proposalSubmitAudienceLabel(viewingParty: ProposalNegotiationParty | undefined): string {
  if (viewingParty === "Client") return "the vendor";
  if (viewingParty === "Vendor") return "the client";
  return "the other party";
}

/** The two teams, which a persisted agreement and a draft builder's spec both carry. */
export type ProposalTeams = Pick<WorkAgreement, "clientTeam" | "vendorTeam">;

/**
 * Who a Submit by `submittingParty` is delivered to: the leader of the other party's team.
 *
 * Decided by the submitting party, not by the reader's legal entity. A client's new draft can have
 * nobody in its own leader slot — a creator without signing authority is left out of it (#3786) — and
 * an entity rule then finds no counterparty, on exactly the draft whose first Submit most needs to say
 * who receives it. The party submitting is always known.
 *
 * Undefined when that team is masked from this viewer or has no leader yet; the dialog then names the
 * party alone.
 */
export function proposalSubmitRecipientMember(
  teams: ProposalTeams | null | undefined,
  submittingParty: ProposalNegotiationParty | undefined,
): EntityMember | undefined {
  if (!teams || !submittingParty) return undefined;
  const counterpartyTeam = submittingParty === "Client" ? teams.vendorTeam : teams.clientTeam;
  if (Masked.isMasked(counterpartyTeam)) return undefined;
  return counterpartyTeam?.leader;
}

/** What a saved change is still waiting for on an active proposal: the submission that shows it. */
export function proposalUnsubmittedChangeStatus(viewingParty: ProposalNegotiationParty | undefined): string {
  return `not yet submitted to ${proposalSubmitAudienceLabel(viewingParty)}`;
}

/**
 * True on a negotiation's very first submission to the counterparty: nothing has published before
 * (no prior submission to diff against) and this draft is not revising an already-hired work
 * agreement (which has real prior state of its own — the live terms — to diff against). Listing every
 * populated field as a "change" on that first submission just restates the whole proposal, so the
 * submit confirmation drops the list rather than describe a change that never happened (#4952).
 *
 * Every later submission — a re-submission, or a revision's first submission against its agreed
 * baseline — keeps its summary, because each has a real prior value behind it.
 */
export function isInitialProposalSubmission(envelope: ProposalCollaborationEnvelope): boolean {
  return (
    !proposalSelectiveTranchePublicationApplies(envelope) && !proposalNegotiationRevisesWorkAgreement(envelope)
  );
}

/**
 * The queued work as the rows a reader will be shown: content operations first, then reviewer changes,
 * net of a field's repeated edits.
 *
 * Exported on its own because two surfaces now describe the same queue — the Save confirmation, which
 * asks before sending it, and each field's history, which previews the history it is about to
 * become (#5133). Deriving both from this one function is what keeps the preview and the dialog's
 * list from itemising the same pending work differently.
 */
export function describeProposalPendingChanges(args: {
  operations: readonly ProposalContentOperation[];
  reviewerChanges: readonly ProposalReviewerChange[];
  context?: ProposalChangeDescriptionContext;
}): ProposalChangeDescriptor[] {
  const context = args.context ?? {};
  // Described as a queue, not one operation at a time, so a document replacement is one row (#5562).
  const contentDescriptors = describeProposalOperations(args.operations, context);
  const reviewerDescriptors = args.reviewerChanges.map((change) => describeProposalReviewerChange(change, context));
  return dedupeByKeyKeepingLast([...contentDescriptors, ...reviewerDescriptors]);
}

/** Describes everything a Save will persist: content operations first, then reviewer changes. */
export function buildProposalSaveSharePlan(args: {
  operations: readonly ProposalContentOperation[];
  reviewerChanges: readonly ProposalReviewerChange[];
  audienceLabel: string;
  context?: ProposalChangeDescriptionContext;
}): ProposalChangeSharePlan {
  const descriptors = describeProposalPendingChanges(args);
  const reviewerChangeCount = descriptors.filter((descriptor) => descriptor.path === reviewerDescriptorPath).length;

  return {
    descriptors,
    lines: formatProposalChangeLines(descriptors),
    audienceLabel: args.audienceLabel,
    contentChangeCount: descriptors.length - reviewerChangeCount,
    reviewerChangeCount,
  };
}

/**
 * A field edited repeatedly queues one operation per edit; the user cares about the net change, so the
 * last write for a key wins — the same rule the operation queue itself applies when it merges.
 */
function dedupeByKeyKeepingLast(descriptors: readonly ProposalChangeDescriptor[]): ProposalChangeDescriptor[] {
  const byKey = new Map<string, ProposalChangeDescriptor>();
  for (const descriptor of descriptors) {
    const existing = byKey.get(descriptor.key);
    // Keep the earliest "previous" value so a run of edits reads from where the field started.
    byKey.set(descriptor.key, existing ? { ...descriptor, previousText: existing.previousText ?? descriptor.previousText } : descriptor);
  }
  return [...byKey.values()];
}
